// Runtime polyfills — MUST load before any app/vendor module that calls these.
// `Array.prototype.at` / `String.prototype.at` are Chrome 92+ (mid-2021); the
// app runs inside native WebViews that on older Android devices ship a pre-92
// engine, where every `.at()` (call sites here + `marked`/`streamdown`) throws
// "X.at is not a function" (JUISCI-APP-2D2/2D4). Vite transpiles syntax but
// never injects method polyfills, so import them explicitly. core-js installs
// conditionally (no-op on engines that already have them).
import "core-js/actual/array/at";
import "core-js/actual/string/at";
import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import { z } from "zod";
import { captureShareAttribution } from "./tools/analytics/shareAttribution";
import { auth } from "./services/firebase";
import {
  captureFirstTouch,
  removeInstallWallMarker,
} from "./tools/analytics/firstTouch";
import App from "./App";
import { env } from "./config/env";
import "./config/i18n";
import { sessionStore } from "./lib/store";
import { takeHandedQuestion } from "./domains/gpt/questionHandoff";
import { startBundleFreshness } from "./bundleFreshness";

// First, before Sentry, Mixpanel and the router read the URL: a question
// handed over in the fragment leaves it (see questionHandoff.ts).
takeHandedQuestion();

// Service worker update cadence.
//
// vite-plugin-pwa's registerSW.js registers sw.js and never reloads the page:
// with injectRegister "script-defer" it holds no update logic. The browser
// re-checks sw.js on a navigation, and here on each background<->foreground
// transition, because native WebView sessions stay open for days without a
// navigation (JUISCI-APP-2AF). A new worker takes control at once (skipWaiting,
// clientsClaim). The running page keeps its bundle: bundleFreshness.ts decides
// when to reload. `serviceWorker` is absent in insecure contexts and sw.js
// only exists in production builds, so this no-ops elsewhere.
if (typeof navigator.serviceWorker !== "undefined") {
  void navigator.serviceWorker.ready.then((registration) => {
    document.addEventListener("visibilitychange", () => {
      // A failed check is a state of the device, not of the code: offline, or
      // an update already in progress (WebKit "newestWorker is null",
      // JUISCI-APP-S). The next transition checks again, and bundleFreshness.ts
      // reports a page that stays stale.
      registration.update().catch(() => undefined);
    });
  });
}

// White-screen recovery net (web-side, ships over-the-air — no app release).
//
// On resume the React tree can be gone while JS is still running: an uncaught
// render error unmounts the whole tree, or a teardown leaves #root empty — the
// white screen the user otherwise has to kill-and-relaunch out of. (The harder
// case, iOS killing the entire WebContent process, halts JS altogether and can
// only be recovered natively — see AppViewController.verifyWebContentAliveOrReload.)
// Here we catch only what JS can still observe: a root that mounted and then went
// empty. On return to foreground we force a full reload, gated two ways: we
// require a prior successful mount (so a slow cold start never triggers it) and
// throttle via sessionStorage (so a genuinely broken bundle can't reload-loop).
let everMounted = false;
const lastWhiteScreenReloadAt = sessionStore(
  "white-screen-reload-at",
  0,
  z.number()
);

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState !== "visible") return;
  const root = document.querySelector("#root");
  if (root === null) return;
  if (root.childElementCount > 0) {
    everMounted = true;
    return;
  }
  if (!everMounted) return;
  const now = Date.now();
  if (now - lastWhiteScreenReloadAt.get() < 10_000) return;
  lastWhiteScreenReloadAt.set(now);
  globalThis.location.reload();
});

const environment = env.appEnv;

declare const __SENTRY_RELEASE__: string;
declare const __BUNDLE_BUILT_AT__: number;

if (
  environment === "production" &&
  !globalThis.location.hostname.includes("localhost")
) {
  Sentry.init({
    release: __SENTRY_RELEASE__,
    environment,
    dsn: "https://50c4a68cbe63eb7e2cae8057f056a66a@o4512106769219584.ingest.de.sentry.io/4512106819027024",
    integrations: [Sentry.captureConsoleIntegration({ levels: ["error"] })],
    normalizeDepth: 6,
    denyUrls: [
      // VitePWA's auto-generated SW registration has no error handling.
      // On mobile, registration fails harmlessly (offline, quota, etc.)
      // producing "Error: Rejected" noise. (JUISCI-APP-V3)
      /registerSW\.js/,
    ],
    ignoreErrors: [
      // Native bridge callbacks use random names (e.g. requestPushAuthorizationCallback_abc123)
      // registered on window. If the page navigates before native responds, the callback
      // is gone and native's evaluateJavaScript throws a harmless ReferenceError.
      // The JS-side promise already times out independently. (JUISCI-APP-138)
      /Can't find variable: \w+Callback_[a-z0-9]+/,
      // iOS drops IndexedDB connections during cold loads / backgrounding.
      // Firebase persistence recovers via fallback, so these are harmless.
      "Connection to Indexed Database server lost", // JUISCI-APP-QQ
      "The database connection is closing", // JUISCI-APP-10Y
      "Attempt to delete range from database without an in-progress transaction", // JUISCI-APP-SB
      // Mobile Safari fires unhandled AbortError when React Query cancels
      // in-flight fetches on unmount/navigation. Harmless. (JUISCI-APP-RP)
      "AbortError",
      // VitePWA registers sw.js for offline caching; on flaky networks the fetch
      // fails with an unhandled rejection. App works fine without it. (JUISCI-APP-YY)
      "Failed to register a ServiceWorker",
      // Firebase Installations fails to auth on iOS when IndexedDB is cleared during
      // backgrounding. Firebase recovers internally; harmless noise. (JUISCI-APP-1AR)
      "installations/request-failed",
      // Same root cause as above, but the raw TypeError reaches console.error before
      // Firebase wraps it. captureConsoleIntegration picks up this form.
      // Chrome: "Failed to fetch" (JUISCI-APP-124). Safari: "Load failed" (JUISCI-APP-10P).
      "Failed to fetch (firebaseinstallations.googleapis.com)",
      "Load failed (firebaseinstallations.googleapis.com)",
      // CEFSharp (.NET Chromium embedders) fire unhandled rejections for internal
      // DOM operations. Not our code, no stacktrace, not actionable. (JUISCI-APP-V7)
      /Object Not Found Matching Id/,
      // iOS Safari 15 / iOS WebView drops the stacktrace when an unhandledrejection
      // has a primitive (undefined/null) reason — Sentry collapses every such event
      // into the same group with no value and no stack. Affects ~100 users on iOS
      // 15.0.2, never reproducible on modern browsers. Not actionable. (JUISCI-APP-233)
      "Non-Error promise rejection captured with value: undefined",
    ],
  });
}

// After Sentry.init: the freshness check tags the scope. `build.json` only
// exists in a build (vite.config.ts, deployedBuildPlugin).
if (import.meta.env.PROD) {
  startBundleFreshness({
    release: __SENTRY_RELEASE__,
    builtAt: __BUNDLE_BUILT_AT__,
  });
}

// Enable :active pseudo-class on iOS Safari (requires a touchstart listener)
document.addEventListener("touchstart", () => {}, { passive: true });

// Before the first render: `SharedBySheet` removes `shared` once mounted, and
// the sharer it names is the credit a later sign-up carries.
captureShareAttribution(globalThis.location, async () => {
  await auth.authStateReady();
  return auth.currentUser !== null;
});
captureFirstTouch({
  location: globalThis.location,
  referrer: document.referrer,
});
removeInstallWallMarker();

const rootElement = document.querySelector("#root");
if (rootElement === null) throw new Error("Root element not found");
const root = createRoot(rootElement);

root.render(<App />);

// Arm the white-screen net: flip `everMounted` once the app has actually
// painted content. A MutationObserver (not a single rAF) so lazy/Suspense
// trees that take several frames to commit still mark the mount.
if (rootElement.childElementCount > 0) {
  everMounted = true;
} else {
  const mountObserver = new MutationObserver(() => {
    if (rootElement.childElementCount > 0) {
      everMounted = true;
      mountObserver.disconnect();
    }
  });
  mountObserver.observe(rootElement, { childList: true });
}
